<template>
  <div class="lots-map">
    <div v-if="showInfoWindow" class="info-window-container">
      <LotInfoWindow
        :services="selectedLot.services"
        :aptitude="selectedLot.aptitude"
        @closeButtonClick="handleInfoWindowCloseClick"
        @windowClick="handleWindowClick"
      />
    </div>
    <transition name="fade">
      <div v-if="!lotsLoaded || !mapInitialized" class="loader-container">
        <div class="loader"></div>
      </div>
    </transition>
    <div class="map-container">
      <div id="google-maps" style="height: 100%; border-radius: 1rem;"></div>
    </div>
    <div id="legend" class="legend-container">
      <div class="legend-item">
        <div class="legend-icon" style="background-color: #4E9306;"></div>
        Agrícola
      </div>
      <div class="legend-item">
        <div class="legend-icon" style="background-color: #E5A20A;"></div>
        Ganadera
      </div>
    </div>
    <div id="clear-button" class="button-container" :class="{ 'visible': !hideClearButton }">
      <base-button id="clear-filter-button" @click="handleClearFilterButtonCLick">
        Limpiar parametros de busqueda
      </base-button>
    </div>
    <CustomMarker
      v-if="mapInitialized"
      v-for="(lot, index) in lots"
      :key="`${index}-${lot.id}-${lot.aptitude}`"
      :map="map"
      :markerConstructor="AdvancedMarkerElement"
      @markerClick="handleMarkerClick"
      :lot="lot"
      :selectedLot="selectedLot"
    />
  </div>
</template>

<script>
import _isEmpty from 'lodash/isEmpty'
import mixpanel from "mixpanel-browser"
import CustomMarker from './CustomMarker.vue'
import LotInfoWindow from './LotInfoWindow.vue'

export default {
  components: {
    CustomMarker,
    LotInfoWindow
  },
  data() {
    return {
      map: {},
      AdvancedMarkerElement: {},
      mapInitialized: false,
      selectedLot: {},
      infoWindow: {},
      showInfoWindow: false,
      LatLngBounds: {},
      LatLng: {}
    }
  },
  props: {
    lots: {
      type: Array,
      default: () => []
    },
    lotsLoaded: {
      type: Boolean,
      required: true
    },
    mapBounds: {
      type: Array,
      required: true
    },
    hideClearButton: {
      type: Boolean,
      required: true
    }
  },
  async mounted() {
    const { AdvancedMarkerElement } = await this.loadMapsLibrary('marker')

    this.AdvancedMarkerElement = AdvancedMarkerElement

    await this.initMap()
  },
  methods: {
    async initMap() {
      const maps = await this.loadMapsLibrary('maps')
      const { ControlPosition, LatLngBounds, LatLng } = await this.loadMapsLibrary('core')
      this.LatLngBounds = LatLngBounds
      this.LatLng = LatLng

      this.map = new maps.Map(document.getElementById("google-maps"), {
        center: { lat: -34.738154, lng: -62.970140 },
        zoom: 5,
        mapId: process.env.VUE_APP_GOOGLE_MAPS_MAP_ID,
        mapTypeId: 'hybrid',
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: ControlPosition.RIGHT_TOP
        }
      })

      this.infoWindow = new maps.InfoWindow({
        headerDisabled: true
      })

      this.map.addListener('click', (event) => {
        this.closeInfoWindow()
      })

      const legend = document.getElementById("legend")
      const clearButton = document.getElementById("clear-button")

      this.map.controls[ControlPosition.LEFT_TOP].push(legend)
      this.map.controls[ControlPosition.TOP_CENTER].push(clearButton)

      if (this.lots.length !== 0) this.setMapBoundsToLots()

      const listener = this.map.addListener('tilesloaded', () => {
        this.mapInitialized = true
        listener.remove()
      })
    },
    setMapBoundsToLots() {
      const bounds = new this.LatLngBounds()
      this.lots.forEach(lot => {
        const { lat, lng } = lot.position
        bounds.extend(new google.maps.LatLng(lat, lng))
      })
      this.map.fitBounds(bounds)
    },
    setMapBoundsToBoundObj(bounds) {
      const sw = new this.LatLng(bounds[0].latitude, bounds[0].longitude)
      const ne = new this.LatLng(bounds[1].latitude, bounds[1].longitude)
      const latLngBounds = new this.LatLngBounds(sw, ne)
      this.map.fitBounds(latLngBounds)
    },
    closeInfoWindow() {
      this.showInfoWindow = false
      this.selectedLot = {}
    },
    openInfoWindow(lot) {
      this.selectedLot = lot
      this.showInfoWindow = true
    },
    handleInfoWindowCloseClick() {
      this.closeInfoWindow()
    },
    handleMarkerClick(lot) {
      mixpanel.track('Custom marker click', {
        lotTitle: lot.title,
        lotNearestCity: lot.nearest_city,
        lotArea: lot.area,
        lotValue: lot.value,
        lotValueUnit: lot.value_unit
      })
      this.openInfoWindow(lot)
    },
    handleWindowClick() {
      mixpanel.track('Info window click', {
        lotTitle: this.selectedLot.title,
        lotNearestCity: this.selectedLot.nearest_city,
        lotArea: this.selectedLot.area,
        lotValue: this.selectedLot.value,
        lotValueUnit: this.selectedLot.value_unit
      })
      this.$emit('selectLot', this.selectedLot)
    },
    handleClearFilterButtonCLick() {
      this.$emit('cleanFilters')
    }
  },
  watch: {
    lots(newVal) {
      if(newVal.length !== 0) {
        this.setMapBoundsToLots()
      }
    },
    mapBounds(newVal) {
      if(!_isEmpty(newVal)) {
        this.setMapBoundsToBoundObj(newVal)
      }
    }
  }
}
</script>
<style>
.lots-map {
  width: 100%;
  height: 75dvh;
  border-radius: 1rem;
}

@media (max-width: 767px) {
  .lots-map {
    height: 100dvh;
  }
}

.info-window-container {
  max-width: 700px;
  width: 90%;
  height: 40%;
  position: absolute;
  bottom: 5%;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
  z-index: 10;
  border-radius: 1rem;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.5);
}

.loader-container {
  text-align: -webkit-center;
  align-content: center;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 10;
  background-color: #C8C8C8;
  border-radius: 1rem;
}

.map-container {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0px;
  left: 0px;
}

.legend-container {
  background-color: rgba(255, 255, 255, 0.5);
  margin: 1rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  font-family: "clearsans", sans-serif;
  transition: background-color 0.25s;
}

.legend-container:hover {
  background-color: rgba(255, 255, 255, 1);
}

.legend-item {
  display: flex;
  padding: 0.15rem;
}

.legend-icon {
  width: 1rem;
  height: 1rem;
  border-radius: 0.5rem;
  margin-right: 0.15rem;
}

.button-container {
  background-color: rgba(255, 255, 255, 0.8);
  margin: 0 1rem;
  padding: 0.5rem;
  border-radius: 0 0 1rem 1rem;
  font-family: "clearsans", sans-serif;
  transition: background-color 0.25s;
  display: none;
}

.button-container.visible {
  display: block;
}

#clear-filter-button {
  background-color: #4E9306;
  color: white;
  border-width: 0px;
  padding: 0.25rem 0.5rem;
  opacity: 0.8;
  transition: opacity 0.25s;
}

#clear-filter-button:hover {
  opacity: 1;
}

.button-container:has(#clear-filter-button:hover) {
  background-color: rgba(255, 255, 255, 1);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>